import escape from "lodash/escape";

import CONSTANTS from "@/Constants.js";

const { ALLOWED_PHOTO_EXTENSIONS, ALLOWED_PHOTO_MIME_TYPES } = CONSTANTS;

// Shared by every photo file input. Constants carries why these are MIME types.
export const PHOTO_ACCEPT: string = ALLOWED_PHOTO_MIME_TYPES.join(",");

const NAMES_SHOWN = 5;

export interface PickedPhotos {
  storable: File[];
  rejected: string[];
}

function extensionOf(fileName: string): string {
  return fileName.slice(fileName.lastIndexOf(".") + 1).toLowerCase();
}

// A dialog's MIME filter is wider than the server's extension allowlist, so a
// pick can be legal in the picker and unstorable on arrival. Those files have to
// be dropped before the upload rather than sent: MediaUpload reserves a batch in
// one request, and Commands::DigitalAssets::ReserveAssets rejects the entire
// call on a single unknown extension, so one stray file takes the good ones
// down with it.
export function partitionPhotos(files: File[]): PickedPhotos {
  const storable: File[] = [];
  const rejected: string[] = [];

  files.forEach((file) => {
    if (ALLOWED_PHOTO_EXTENSIONS.includes(extensionOf(file.name))) {
      storable.push(file);
    } else {
      rejected.push(file.name);
    }
  });

  return { storable, rejected };
}

// Names are escaped because window.notify hands its message to Toastify with
// escapeMarkup: false, so the text is assigned through innerHTML — and callers
// rely on that to pass deliberate <br/>, so the sink cannot escape for them. A
// file name is whatever the person naming the file chose.
//
// Only the first few are named: "select all" over a library of HEICs would
// otherwise be a wall of text for the toast's full duration.
export function unsupportedPhotoMessage(fileNames: string[]): string {
  const allowed = ALLOWED_PHOTO_EXTENSIONS.map(
    (extension: string) => `.${extension}`
  ).join(", ");
  const named = fileNames.slice(0, NAMES_SHOWN).map(escape).join(", ");
  const rest = fileNames.length - NAMES_SHOWN;
  const subject = rest > 0 ? `${named} and ${rest} more` : named;

  return `${subject} could not be uploaded. Photos must be ${allowed}.`;
}
